import React, { useEffect, useMemo, useState } from 'react';
import {
    Equipment,
    LaborRate,
    ProcessTemplate,
    SetupLaborEntry,
    StationEquipmentLine,
} from '../types';
import { inventoryApi, processTemplatesApi } from '../services/api';
import { getIsAdmin } from '../utils/auth';
import { computeLineCost, formatCurrency } from '../utils/costing';
import EquipmentBrowser from './EquipmentBrowser';

interface DraftTemplate {
    id: string;
    name: string;
    category: string;
    description: string;
    items: StationEquipmentLine[];
    setupLabor: SetupLaborEntry[];
    isNew: boolean;
}

const blankDraft = (): DraftTemplate => ({
    id: '',
    name: '',
    category: '',
    description: '',
    items: [],
    setupLabor: [],
    isNew: true,
});

const fromTemplate = (template: ProcessTemplate): DraftTemplate => ({
    id: template.id,
    name: template.name,
    category: template.category ?? '',
    description: template.description ?? '',
    items: template.items.map((item) => ({ ...item })),
    setupLabor: template.setup_labor.map((entry) => ({ ...entry })),
    isNew: false,
});

const toPayload = (draft: DraftTemplate) => ({
    name: draft.name.trim(),
    category: draft.category.trim() || null,
    description: draft.description.trim() || null,
    items: draft.items,
    setup_labor: draft.setupLabor,
});

const ProcessTemplatesPage: React.FC = () => {
    const isAdmin = getIsAdmin();
    const [templates, setTemplates] = useState<ProcessTemplate[]>([]);
    const [equipment, setEquipment] = useState<Equipment[]>([]);
    const [laborRates, setLaborRates] = useState<LaborRate[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [draft, setDraft] = useState<DraftTemplate | null>(null);
    const [pickingEquipment, setPickingEquipment] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const [list, equipmentList, rates] = await Promise.all([
                    processTemplatesApi.list(),
                    inventoryApi.listEquipment(),
                    inventoryApi.listLaborRates(),
                ]);
                setTemplates(list);
                setEquipment(equipmentList);
                setLaborRates(rates);
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : 'Failed to load process templates'
                );
            } finally {
                setIsLoading(false);
            }
        };
        void load();
    }, []);

    const equipmentById = useMemo(
        () => new Map(equipment.map((e) => [e.id, e])),
        [equipment]
    );

    const beginCreate = () => {
        setEditingId('__new__');
        setDraft(blankDraft());
        setPickingEquipment(false);
    };

    const beginEdit = (template: ProcessTemplate) => {
        setEditingId(template.id);
        setDraft(fromTemplate(template));
        setPickingEquipment(false);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setDraft(null);
        setPickingEquipment(false);
    };

    const updateDraft = (patch: Partial<DraftTemplate>) => {
        setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
    };

    const addItem = (item: Equipment) => {
        setDraft((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                items: [
                    ...prev.items,
                    {
                        equipment_id: item.id,
                        quantity: 1,
                        is_free_issued: false,
                    },
                ],
            };
        });
        setPickingEquipment(false);
    };

    const updateItem = (index: number, patch: Partial<StationEquipmentLine>) => {
        setDraft((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                items: prev.items.map((line, i) =>
                    i === index ? { ...line, ...patch } : line
                ),
            };
        });
    };

    const removeItem = (index: number) => {
        setDraft((prev) => {
            if (!prev) return prev;
            return { ...prev, items: prev.items.filter((_, i) => i !== index) };
        });
    };

    const addSetupLaborRow = () => {
        setDraft((prev) => {
            if (!prev) return prev;
            const used = new Set(prev.setupLabor.map((e) => e.role));
            const nextRole = laborRates.find((r) => !used.has(r.role))?.role ?? '';
            return {
                ...prev,
                setupLabor: [...prev.setupLabor, { role: nextRole, hours: 0 }],
            };
        });
    };

    const updateSetupLaborRow = (index: number, patch: Partial<SetupLaborEntry>) => {
        setDraft((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                setupLabor: prev.setupLabor.map((entry, i) =>
                    i === index ? { ...entry, ...patch } : entry
                ),
            };
        });
    };

    const removeSetupLaborRow = (index: number) => {
        setDraft((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                setupLabor: prev.setupLabor.filter((_, i) => i !== index),
            };
        });
    };

    const handleSave = async () => {
        if (!draft) return;
        const trimmedName = draft.name.trim();
        if (!trimmedName) {
            setError('Name is required');
            return;
        }
        if (draft.isNew && !draft.id.trim()) {
            setError('Id is required');
            return;
        }
        setIsSaving(true);
        setError(null);
        try {
            const payload = toPayload(draft);
            let saved: ProcessTemplate;
            if (draft.isNew) {
                saved = await processTemplatesApi.create({
                    id: draft.id.trim(),
                    ...payload,
                });
                setTemplates((prev) =>
                    [...prev, saved].sort((a, b) => a.name.localeCompare(b.name))
                );
            } else {
                saved = await processTemplatesApi.update(draft.id, payload);
                setTemplates((prev) =>
                    prev
                        .map((t) => (t.id === saved.id ? saved : t))
                        .sort((a, b) => a.name.localeCompare(b.name))
                );
            }
            cancelEdit();
        } catch (err) {
            setError(extractApiError(err, 'Failed to save process template'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (template: ProcessTemplate) => {
        const confirmed = window.confirm(
            `Delete process template "${template.name}"? This cannot be undone.`
        );
        if (!confirmed) return;
        setError(null);
        try {
            await processTemplatesApi.remove(template.id);
            setTemplates((prev) => prev.filter((t) => t.id !== template.id));
            if (editingId === template.id) cancelEdit();
        } catch (err) {
            setError(extractApiError(err, 'Failed to delete process template'));
        }
    };

    if (isLoading) {
        return (
            <div className="ff-mono text-xs text-[#141414]/55">
                Loading process templates…
            </div>
        );
    }

    return (
        <div>
            {error && (
                <div className="mb-4 border border-[#c2251a] bg-[#c2251a]/10 text-[#c2251a] px-3 py-2 ff-mono text-xs">
                    {error}
                </div>
            )}

            {isAdmin && editingId !== '__new__' && (
                <div className="mb-4">
                    <button
                        type="button"
                        onClick={beginCreate}
                        className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 bg-[#141414] text-[#f3efe6] hover:bg-[#e0461e] transition"
                    >
                        + New process template
                    </button>
                </div>
            )}

            {editingId === '__new__' && draft && (
                <div className="mb-4 border border-[#141414] bg-white">
                    <TemplateEditor
                        draft={draft}
                        equipmentById={equipmentById}
                        equipment={equipment}
                        laborRates={laborRates}
                        pickingEquipment={pickingEquipment}
                        onOpenPicker={() => setPickingEquipment(true)}
                        onClosePicker={() => setPickingEquipment(false)}
                        onAddItem={addItem}
                        onUpdateDraft={updateDraft}
                        onUpdateItem={updateItem}
                        onRemoveItem={removeItem}
                        onAddSetupLaborRow={addSetupLaborRow}
                        onUpdateSetupLaborRow={updateSetupLaborRow}
                        onRemoveSetupLaborRow={removeSetupLaborRow}
                        onSave={handleSave}
                        onCancel={cancelEdit}
                        isSaving={isSaving}
                    />
                </div>
            )}

            {templates.length === 0 && editingId !== '__new__' ? (
                <div className="ff-mono text-xs text-[#141414]/55 border border-dashed border-[#141414]/25 px-4 py-6 text-center">
                    No process templates yet
                    {isAdmin && '. Create one to bundle equipment for reuse on conveyor stations.'}
                </div>
            ) : (
                <ul className="space-y-2">
                    {templates.map((template) =>
                        editingId === template.id && draft ? (
                            <li
                                key={template.id}
                                className="border border-[#141414] bg-white"
                            >
                                <TemplateEditor
                                    draft={draft}
                                    equipmentById={equipmentById}
                                    equipment={equipment}
                                    laborRates={laborRates}
                                    pickingEquipment={pickingEquipment}
                                    onOpenPicker={() => setPickingEquipment(true)}
                                    onClosePicker={() => setPickingEquipment(false)}
                                    onAddItem={addItem}
                                    onUpdateDraft={updateDraft}
                                    onUpdateItem={updateItem}
                                    onRemoveItem={removeItem}
                                    onAddSetupLaborRow={addSetupLaborRow}
                                    onUpdateSetupLaborRow={updateSetupLaborRow}
                                    onRemoveSetupLaborRow={removeSetupLaborRow}
                                    onSave={handleSave}
                                    onCancel={cancelEdit}
                                    isSaving={isSaving}
                                />
                            </li>
                        ) : (
                            <li
                                key={template.id}
                                className="border border-[#141414]/20 bg-white"
                            >
                                <TemplateRow
                                    template={template}
                                    equipmentById={equipmentById}
                                    isAdmin={isAdmin}
                                    onEdit={() => beginEdit(template)}
                                    onDelete={() => handleDelete(template)}
                                />
                            </li>
                        )
                    )}
                </ul>
            )}
        </div>
    );
};

interface TemplateRowProps {
    template: ProcessTemplate;
    equipmentById: Map<string, Equipment>;
    isAdmin: boolean;
    onEdit: () => void;
    onDelete: () => void;
}

const TemplateRow: React.FC<TemplateRowProps> = ({
    template,
    equipmentById,
    isAdmin,
    onEdit,
    onDelete,
}) => {
    const unitCount = template.items.reduce((acc, item) => acc + item.quantity, 0);
    const missingEquipment = template.items.some((item) => !equipmentById.has(item.equipment_id));
    return (
        <div className="flex items-center gap-3 px-4 py-3">
            <div className="flex-1 min-w-0">
                <div className="ff-serif text-lg leading-tight text-[#141414] truncate">
                    {template.name}
                </div>
                <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55 mt-0.5">
                    {template.category ? `${template.category} · ` : ''}
                    {template.items.length} item
                    {template.items.length === 1 ? '' : 's'} · {unitCount} unit
                    {unitCount === 1 ? '' : 's'} · id {template.id}
                </div>
                {template.description && (
                    <div className="ff-mono text-xs text-[#141414]/70 mt-1 line-clamp-2">
                        {template.description}
                    </div>
                )}
                {missingEquipment && (
                    <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#b87a1a] mt-1">
                        Some equipment is no longer in the catalog
                    </div>
                )}
            </div>
            <div className="flex gap-2 shrink-0">
                {isAdmin ? (
                    <>
                        <button
                            type="button"
                            onClick={onEdit}
                            className="ff-mono text-[10px] uppercase tracking-wider px-3 py-1.5 border border-[#141414]/25 text-[#141414]/70 hover:bg-[#141414]/5 transition"
                        >
                            Edit
                        </button>
                        <button
                            type="button"
                            onClick={onDelete}
                            className="ff-mono text-[10px] uppercase tracking-wider px-3 py-1.5 border border-[#c2251a]/30 text-[#c2251a] hover:bg-[#c2251a]/10 transition"
                        >
                            Delete
                        </button>
                    </>
                ) : (
                    <button
                        type="button"
                        onClick={onEdit}
                        className="ff-mono text-[10px] uppercase tracking-wider px-3 py-1.5 border border-[#141414]/25 text-[#141414]/70 hover:bg-[#141414]/5 transition"
                    >
                        View
                    </button>
                )}
            </div>
        </div>
    );
};

interface TemplateEditorProps {
    draft: DraftTemplate;
    equipmentById: Map<string, Equipment>;
    equipment: Equipment[];
    laborRates: LaborRate[];
    pickingEquipment: boolean;
    onOpenPicker: () => void;
    onClosePicker: () => void;
    onAddItem: (item: Equipment) => void;
    onUpdateDraft: (patch: Partial<DraftTemplate>) => void;
    onUpdateItem: (index: number, patch: Partial<StationEquipmentLine>) => void;
    onRemoveItem: (index: number) => void;
    onAddSetupLaborRow: () => void;
    onUpdateSetupLaborRow: (index: number, patch: Partial<SetupLaborEntry>) => void;
    onRemoveSetupLaborRow: (index: number) => void;
    onSave: () => void;
    onCancel: () => void;
    isSaving: boolean;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({
    draft,
    equipmentById,
    equipment,
    laborRates,
    pickingEquipment,
    onOpenPicker,
    onClosePicker,
    onAddItem,
    onUpdateDraft,
    onUpdateItem,
    onRemoveItem,
    onAddSetupLaborRow,
    onUpdateSetupLaborRow,
    onRemoveSetupLaborRow,
    onSave,
    onCancel,
    isSaving,
}) => {
    // Templates don't carry worker_type; the editor previews labor cost at the
    // internal rate. The actual worker type is picked per station after apply.
    const ratesByRole = useMemo(
        () => new Map(laborRates.map((r) => [r.role, r.internal_rate])),
        [laborRates]
    );
    const totals = useMemo(() => {
        let goods = 0;
        for (const line of draft.items) {
            const cost = computeLineCost(line, equipment, laborRates);
            if (!cost) continue;
            goods += cost.goods;
        }
        let labor = 0;
        for (const entry of draft.setupLabor) {
            const rate = ratesByRole.get(entry.role) ?? 0;
            labor += entry.hours * rate;
        }
        return { goods, labor, total: goods + labor };
    }, [draft.items, draft.setupLabor, equipment, laborRates, ratesByRole]);

    return (
        <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Field label="Id">
                    {draft.isNew ? (
                        <input
                            type="text"
                            value={draft.id}
                            onChange={(e) =>
                                onUpdateDraft({
                                    id: e.target.value.toLowerCase().replace(/\s+/g, '-'),
                                })
                            }
                            placeholder="welding-robot-x"
                            className="w-full bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1.5 ff-mono text-sm"
                        />
                    ) : (
                        <div className="ff-mono text-sm text-[#141414]/70 py-1.5">
                            {draft.id}
                        </div>
                    )}
                </Field>
                <Field label="Name">
                    <input
                        type="text"
                        value={draft.name}
                        onChange={(e) => onUpdateDraft({ name: e.target.value })}
                        placeholder="Welding with robot X"
                        className="w-full bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1.5 ff-mono text-sm"
                    />
                </Field>
                <Field label="Category">
                    <input
                        type="text"
                        value={draft.category}
                        onChange={(e) => onUpdateDraft({ category: e.target.value })}
                        placeholder="welding"
                        className="w-full bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1.5 ff-mono text-sm"
                    />
                </Field>
                <Field label="Description">
                    <textarea
                        value={draft.description}
                        onChange={(e) => onUpdateDraft({ description: e.target.value })}
                        placeholder="What this process does, and any notes on use"
                        rows={2}
                        className="w-full bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1.5 ff-mono text-sm resize-y"
                    />
                </Field>
            </div>

            <div className="border-t border-[#141414]/15 pt-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55">
                        Equipment ({draft.items.length})
                    </div>
                    {!pickingEquipment && (
                        <button
                            type="button"
                            onClick={onOpenPicker}
                            className="ff-mono text-[10px] uppercase tracking-wider px-2 py-1 border border-[#141414] hover:bg-[#141414] hover:text-[#f3efe6] transition"
                        >
                            + Add equipment
                        </button>
                    )}
                </div>

                {draft.items.length === 0 && !pickingEquipment ? (
                    <div className="ff-mono text-xs text-[#141414]/55 border border-dashed border-[#141414]/25 px-4 py-6 text-center">
                        No equipment yet. Add items to define what this template applies.
                    </div>
                ) : (
                    <div className="divide-y divide-[#141414]/10">
                        {draft.items.map((line, index) => {
                            const item = equipmentById.get(line.equipment_id);
                            const cost = computeLineCost(line, equipment, laborRates);
                            return (
                                <div
                                    key={`${line.equipment_id}-${index}`}
                                    className="grid grid-cols-12 gap-3 items-center py-2.5"
                                >
                                    <div className="col-span-5 min-w-0">
                                        <div className="text-[14px] truncate">
                                            {item?.name ?? line.equipment_id}
                                        </div>
                                        {!item && (
                                            <div className="ff-mono text-[9px] uppercase tracking-wider text-[#b87a1a]">
                                                no longer in catalog
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-span-2">
                                        <input
                                            type="number"
                                            min={1}
                                            step={1}
                                            value={line.quantity}
                                            onChange={(e) => {
                                                const next = Number(e.target.value);
                                                onUpdateItem(index, {
                                                    quantity: Number.isFinite(next) && next > 0
                                                        ? Math.floor(next)
                                                        : 1,
                                                });
                                            }}
                                            className="num-input w-20 bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1 ff-mono text-sm"
                                            aria-label="Quantity"
                                        />
                                    </div>
                                    <div className="col-span-3">
                                        <label className="flex items-center gap-2 ff-mono text-[11px] text-[#141414]/70">
                                            <input
                                                type="checkbox"
                                                checked={line.is_free_issued ?? false}
                                                onChange={(e) =>
                                                    onUpdateItem(index, {
                                                        is_free_issued: e.target.checked,
                                                    })
                                                }
                                            />
                                            Free-issued
                                        </label>
                                    </div>
                                    <div className="col-span-1 text-right ff-mono text-xs text-[#141414]/70">
                                        {cost ? formatCurrency(cost.total) : '—'}
                                    </div>
                                    <div className="col-span-1 text-right">
                                        <button
                                            type="button"
                                            onClick={() => onRemoveItem(index)}
                                            className="ff-mono text-[10px] uppercase tracking-wider text-[#141414]/55 hover:text-[#c2251a] transition"
                                            aria-label="Remove equipment"
                                        >
                                            remove
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {pickingEquipment && (
                    <div className="mt-3 border border-[#141414] bg-[#f8f5ec] p-3">
                        <div className="flex items-center justify-between mb-2">
                            <span className="ff-mono text-[10px] uppercase tracking-[0.2em]">
                                Equipment catalog
                            </span>
                            <button
                                type="button"
                                onClick={onClosePicker}
                                className="ff-mono text-[10px] uppercase tracking-wider text-[#141414]/70 hover:text-[#e0461e] transition"
                            >
                                close
                            </button>
                        </div>
                        <EquipmentBrowser
                            onSelect={onAddItem}
                            selectButtonLabel="Add"
                        />
                    </div>
                )}
            </div>

            <div className="border-t border-[#141414]/15 pt-4 mt-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55">
                        Setup labor ({draft.setupLabor.length})
                    </div>
                    <button
                        type="button"
                        onClick={onAddSetupLaborRow}
                        disabled={laborRates.length === 0}
                        className="ff-mono text-[10px] uppercase tracking-wider px-2 py-1 border border-[#141414] hover:bg-[#141414] hover:text-[#f3efe6] disabled:opacity-40 transition"
                    >
                        + Add role
                    </button>
                </div>

                {draft.setupLabor.length === 0 ? (
                    <div className="ff-mono text-xs text-[#141414]/55 border border-dashed border-[#141414]/25 px-4 py-6 text-center">
                        No setup labor yet. Add roles to estimate the labor needed
                        to set this process up.
                    </div>
                ) : (
                    <div className="divide-y divide-[#141414]/10">
                        {draft.setupLabor.map((entry, index) => {
                            const rate = ratesByRole.get(entry.role) ?? 0;
                            const amount = entry.hours * rate;
                            return (
                                <div
                                    key={`${entry.role}-${index}`}
                                    className="grid grid-cols-12 gap-3 items-center py-2.5"
                                >
                                    <div className="col-span-5">
                                        <select
                                            value={entry.role}
                                            onChange={(e) =>
                                                onUpdateSetupLaborRow(index, {
                                                    role: e.target.value,
                                                })
                                            }
                                            className="w-full bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1 ff-mono text-sm"
                                            aria-label="Role"
                                        >
                                            {laborRates.map((r) => (
                                                <option key={r.role} value={r.role}>
                                                    {r.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-span-3">
                                        <input
                                            type="number"
                                            min={0}
                                            step={0.5}
                                            value={entry.hours}
                                            onChange={(e) => {
                                                const next = Number(e.target.value);
                                                onUpdateSetupLaborRow(index, {
                                                    hours: Number.isFinite(next) && next >= 0
                                                        ? next
                                                        : 0,
                                                });
                                            }}
                                            className="num-input w-24 bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1 ff-mono text-sm"
                                            aria-label="Hours"
                                        />
                                        <span className="ml-1 ff-mono text-[10px] text-[#141414]/55">
                                            hrs
                                        </span>
                                    </div>
                                    <div className="col-span-2 ff-mono text-[11px] text-[#141414]/55">
                                        @ {formatCurrency(rate)}/hr
                                    </div>
                                    <div className="col-span-1 text-right ff-mono text-xs text-[#141414]/70">
                                        {formatCurrency(amount)}
                                    </div>
                                    <div className="col-span-1 text-right">
                                        <button
                                            type="button"
                                            onClick={() => onRemoveSetupLaborRow(index)}
                                            className="ff-mono text-[10px] uppercase tracking-wider text-[#141414]/55 hover:text-[#c2251a] transition"
                                            aria-label="Remove role"
                                        >
                                            remove
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="mt-5 flex items-center justify-between gap-3 border-t-2 border-[#141414] pt-3 flex-wrap">
                <div className="flex items-center gap-6 ff-mono text-sm">
                    <span>
                        <span className="text-[#141414]/50 text-[10px] uppercase tracking-wider mr-2">
                            Capital
                        </span>
                        <span className="font-semibold">{formatCurrency(totals.goods)}</span>
                    </span>
                    <span>
                        <span className="text-[#141414]/50 text-[10px] uppercase tracking-wider mr-2">
                            Labor
                        </span>
                        <span className="font-semibold">{formatCurrency(totals.labor)}</span>
                    </span>
                    <span className="pl-4 border-l border-[#141414]/20">
                        <span className="text-[#141414]/50 text-[10px] uppercase tracking-wider mr-2">
                            Total
                        </span>
                        <span className="font-bold text-[#e0461e] text-base">
                            {formatCurrency(totals.total)}
                        </span>
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isSaving}
                        className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 border border-[#141414]/25 text-[#141414]/70 hover:bg-[#141414]/5 disabled:opacity-50 transition"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={isSaving}
                        className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 bg-[#141414] text-[#f3efe6] hover:bg-[#e0461e] disabled:opacity-50 transition"
                    >
                        {isSaving ? 'Saving…' : draft.isNew ? 'Create' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
    label,
    children,
}) => (
    <label className="block">
        <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55 mb-1">
            {label}
        </div>
        {children}
    </label>
);

const extractApiError = (err: unknown, fallback: string): string => {
    if (typeof err === 'object' && err !== null) {
        const maybeAxios = err as {
            response?: { data?: { detail?: unknown } };
            message?: string;
        };
        const detail = maybeAxios.response?.data?.detail;
        if (typeof detail === 'string') return detail;
        if (typeof maybeAxios.message === 'string') return maybeAxios.message;
    }
    if (err instanceof Error) return err.message;
    return fallback;
};

export default ProcessTemplatesPage;
